import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Clock, Send } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import CursorGlow from "../../components/CursorGlow";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      // Send contact form to backend which will email to hello@africamarket.co
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          to_email: 'hello@africamarket.co'
        })
      });
      
      if (response.ok) {
        toast.success("Message sent! We'll get back to you soon.");
        setFormData({ name: "", email: "", message: "" });
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch (error) {
      // Fallback - show success anyway for UX (message logged for now)
      console.log('Contact form submission:', formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", message: "" });
    }
    
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-emerald-950 text-white overflow-hidden">
      <CursorGlow />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-emerald-950/90 backdrop-blur-md border-b border-emerald-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-20 sm:h-24 w-auto" />
            </Link>
            
            <div className="hidden md:flex items-center gap-10">
              <Link to="/" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Home</Link>
              <Link to="/features" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="text-sm font-medium text-emerald-300 hover:text-white transition-colors">Contact</Link>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" size="sm" className="text-emerald-300 hover:text-white hover:bg-emerald-800/50">Log in</Button></Link>
              <Link to="/pricing"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-6">Sign up</Button></Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            Get in
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-300">
              touch
            </span>
          </h1>
          <p className="text-xl text-emerald-200/80">
            Have a question, feature request, or need help?
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-2xl flex items-center justify-center flex-shrink-0 border border-emerald-500/30">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-lg mb-1">Email</h3>
                  <a href="mailto:hello@africamarket.co" className="text-emerald-400 hover:text-emerald-300 transition-colors block">
                    hello@africamarket.co
                  </a>
                  <a href="mailto:hello@ledgeramt.com" className="text-emerald-400 hover:text-emerald-300 transition-colors block">
                    hello@ledgeramt.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-2xl flex items-center justify-center flex-shrink-0 border border-emerald-500/30">
                  <Clock className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-lg mb-1">Phone</h3>
                  <p className="text-emerald-300/70">+1 929-355-2649</p>
                  <p className="text-emerald-300/70">+231777527159</p>
                </div>
              </div>
              
              <div className="bg-emerald-900/30 rounded-2xl p-5 border border-emerald-700/30">
                <h3 className="font-semibold text-white mb-3">Our Offices</h3>
                <div className="grid gap-4 text-sm">
                  <div>
                    <p className="text-emerald-400 font-medium">Liberia Office</p>
                    <p className="text-emerald-300/70">P.O. Box 283 Omega</p>
                    <p className="text-emerald-300/70">Wehnzon Community</p>
                    <p className="text-emerald-300/70">Paynesville, Liberia</p>
                  </div>
                  <div>
                    <p className="text-emerald-400 font-medium">United States Office</p>
                    <p className="text-emerald-300/70">11 Municipal Drive</p>
                    <p className="text-emerald-300/70">200PMB1027</p>
                    <p className="text-emerald-300/70">Fishers, Indiana 46038, USA</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-950/50 rounded-3xl p-8 border border-emerald-700/30">
              <h2 className="text-xl font-semibold text-white mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-emerald-300/80 mb-2">
                    Name
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    required
                    className="h-12 bg-emerald-900/50 border-emerald-700/50 text-white placeholder:text-emerald-500/50 focus:border-emerald-500 rounded-xl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-emerald-300/80 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    required
                    className="h-12 bg-emerald-900/50 border-emerald-700/50 text-white placeholder:text-emerald-500/50 focus:border-emerald-500 rounded-xl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-emerald-300/80 mb-2">
                    Message
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help?"
                    rows={5}
                    required
                    className="bg-emerald-900/50 border-emerald-700/50 text-white placeholder:text-emerald-500/50 focus:border-emerald-500 rounded-xl"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-12 rounded-xl"
                  disabled={sending}
                >
                  {sending ? (
                    <>Sending...</>
                  ) : (
                    <>
                      Send message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-950 border-t border-emerald-800/30 py-16 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-20 w-auto mb-6" />
              <p className="text-emerald-400/60 text-sm">Simple invoices. Clear cash control.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/features" className="hover:text-emerald-400">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-emerald-400">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/about" className="hover:text-emerald-400">About</Link></li>
                <li><Link to="/contact" className="hover:text-emerald-400">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Account</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/login" className="hover:text-emerald-400">Log in</Link></li>
                <li><Link to="/pricing" className="hover:text-emerald-400">Sign up</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-emerald-800/30 pt-8 text-center text-sm text-emerald-400/50">
            <p>© {new Date().getFullYear()} LedgerAMT. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
